import { ChangeDetectorRef, Component, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BsModalService } from 'ngx-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Observable, Subject } from 'rxjs';
import { BaseComponent } from 'app/components/base.component';
import {
  CampaignQCCallConversation,
  CampaignQCCallHistory,
  ViewCampaignQCCallConversation,
  SmartQcService,
  ViewCampaignQCCallInfo,
  ViewCampaignQCCallAnalyze,
  CampaignQCCallInfo,
  CampaignQCCallAnalyze,
} from 'app/common/services/SmartQC.service';
import {
  Tab,
  buildTabs,
  TabKey,
  buildViewCallAnalyze,
  buildViewCallInfo,
  buildViewCallConversation,
  buildViewCallHistory,
  buildTableColumnsCallHistory,
} from './campaign-call-detail.util';

@Component({
  selector: 'app-smart-qc-campaign-call-detail',
  templateUrl: './campaign-call-detail.component.html',
  styleUrls: ['./campaign-call-detail.component.css'],
  providers: [SmartQcService],
})
export class CampaignCallDetailComponent extends BaseComponent {
  @ViewChild('audioPlayer') audioPlayerRef: ElementRef;
  @ViewChild('conversationContainer') conversationContainerRef: ElementRef;
  private _campaignQCId: string;
  private _campaignQCTicketId: string;
  private _threshold: number = 0.1;
  private _timeUpdateSubject: Subject<number> = new Subject<number>();
  public loading: boolean = true;
  public viewCallInfo: ViewCampaignQCCallInfo;
  public viewCallHistories: CampaignQCCallHistory[] = [];
  public viewCallConversations: ViewCampaignQCCallConversation[] = [];
  public viewCallAnalyze: ViewCampaignQCCallAnalyze | any;
  public selectedConversationIndex: number = 0;
  public activeTabKey: TabKey;
  public TabKey = TabKey;
  public tabs: Tab[];
  public tableHistoryColumns = [];
  protected animationFrameId: any;

  constructor(
    protected changeDetector: ChangeDetectorRef,
    protected toastr: ToastrService,
    protected modalService: BsModalService,
    private router: Router,
    private activeRoute: ActivatedRoute,
    private smartQCService: SmartQcService
  ) {
    super(changeDetector, toastr, modalService);
  }

  ngOnInit(): void {
    this.loading = true;
    this.tabs = buildTabs();
    this.activeTabKey = TabKey.Emotion;
    this.selectedConversationIndex = 0;
    this.tableHistoryColumns = buildTableColumnsCallHistory();
    this._timeUpdateSubject
      .throttle(() => Observable.interval(100))
      .subscribe(currentTime => this.processTimeUpdate(currentTime));

    this.activeRoute.paramMap.subscribe(params => {
      this._campaignQCId = params.get('id');
      this._campaignQCTicketId = params.get('qc_ticket_id');

      Observable.forkJoin([
        this.smartQCService.getCampaignQCCallInfo(this._campaignQCId, this._campaignQCTicketId),
        this.smartQCService.getCampaignQCCallHistory(this._campaignQCId, this._campaignQCTicketId),
        this.smartQCService.getCampaignQCCallConversation(this._campaignQCId, this._campaignQCTicketId),
        this.smartQCService.getCampaignQCCallAnalyze(this._campaignQCId, this._campaignQCTicketId),
      ]).subscribe(
        results => {
          const [callInfoResponse, historiesResponse, conversationResponse, callAnalyzeResponse] = results;

          this.viewCallInfo = buildViewCallInfo(callInfoResponse.data as CampaignQCCallInfo);

          const conversations = (
            conversationResponse.data ? conversationResponse.data.conversations : []
          ) as CampaignQCCallConversation[];
          this.viewCallConversations = conversations.map((conversation, index: number) =>
            buildViewCallConversation(conversation, index)
          );

          const histories = historiesResponse.data as CampaignQCCallHistory[];
          this.viewCallHistories = histories.map(history => buildViewCallHistory(history));

          const callAnalyze = !callAnalyzeResponse.data
            ? null
            : (callAnalyzeResponse.data.analyze_data as CampaignQCCallAnalyze);
          this.viewCallAnalyze = !callAnalyze ? {} : buildViewCallAnalyze(callAnalyze);

          this.loading = false;
        },
        error => {
          this.loading = false;
          this.throwError(error.message);
        },
        () => {
          this.loading = false;
        }
      );
    });
  }

  ngOnDestroy(): void {}

  async handleExportText() {
    return this.smartQCService
      .exportText(this._campaignQCId, this._campaignQCTicketId)
      .then(blob => {
        const link = document.createElement('a');
        const downloadURL = URL.createObjectURL(blob);
        link.href = downloadURL;
        link.download = `calls-text-${this.viewCallInfo.callId}.txt`;
        document.body.appendChild(link);

        link.click();

        document.body.removeChild(link);
        URL.revokeObjectURL(downloadURL);

        this.throwSuccess('Export thành công!');
      })
      .catch(e => {
        this.throwError(e.message);
      });
  }

  async handleExportFull() {
    return this.smartQCService
      .exportFull(this._campaignQCId, this._campaignQCTicketId)
      .then(blob => {
        const link = document.createElement('a');
        const downloadURL = URL.createObjectURL(blob);
        link.href = downloadURL;
        link.download = `calls-full-${this.viewCallInfo.callId}.zip`;
        document.body.appendChild(link);

        link.click();

        document.body.removeChild(link);
        URL.revokeObjectURL(downloadURL);

        this.throwSuccess('Export thành công!');
      })
      .catch(e => {
        this.throwError(e.message);
      });
  }

  handleReAnalysisTicket() {}

  handleGoBack() {
    this.router.navigate(['/smart-qc/campaigns', this._campaignQCId, 'calls']);
  }

  handlePlayConversation(index: number) {
    if (this.audioPlayerRef) {
      this.selectedConversationIndex = index;

      const startTime = this.viewCallConversations[index].startTime;
      const audioPlayerElement = this.audioPlayerRef.nativeElement as HTMLAudioElement;
      audioPlayerElement.currentTime = startTime;
      audioPlayerElement.play();
    }
  }

  handlePlay() {}

  handlePause() {}

  handleTimeUpdate() {
    const audioElement = this.audioPlayerRef.nativeElement;
    const currentTime = Math.floor(audioElement.currentTime);
    this._timeUpdateSubject.next(currentTime);
  }

  processTimeUpdate(currentTime: number) {
    console.log('Time: ', currentTime);

    const containerElement = this.conversationContainerRef.nativeElement;
    const conversationIndex = this.viewCallConversations.findIndex(
      x => currentTime >= x.startTime && currentTime <= x.endTime
    );

    if (conversationIndex !== -1 && conversationIndex !== this.selectedConversationIndex) {
      this.selectedConversationIndex = conversationIndex;

      const conversationElement = containerElement.querySelector(`#conversation${conversationIndex}`);
      const containerRect = containerElement.getBoundingClientRect();
      const elementRect = conversationElement.getBoundingClientRect();
      const thresholdPoint = containerRect.top + containerElement.clientHeight * this._threshold;
      if (elementRect.bottom > thresholdPoint) {
        conversationElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    }
  }

  handleActiveTab(tabKey: TabKey) {
    const tab = this.tabs.find(t => t.key === tabKey);
    if (!tab.disabled) {
      this.activeTabKey = tabKey;
    }
  }

  setPage(page: number) {
    console.log('run');
  }

  goPage2(page: string) {
    let toPage: any = page;
    if (!toPage) {
      toPage = 1;
    }

    if (Number.isInteger(toPage) && toPage <= 0) {
      toPage = 1;
    }

    this.setPage(toPage);
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/admin/icontact/smart-qc/campaign/call-detail/campaign-call-detail.component.ts